const visibleBatchSize = () => window.innerWidth >= 990 ? 10 : window.innerWidth >= 750 ? 6 : 8;

class IkCatalogMore extends HTMLElement {
  #controller;
  #grid;
  #button;
  #status;
  #cards = [];
  #ids = new Set();
  #pages = new Set();
  #nextUrl = '';
  #batches = 1;
  #loading = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#grid = this.querySelector('[data-ik-catalog-batch]');
    this.#button = this.querySelector('[data-more-button]');
    this.#status = this.querySelector('[data-more-status]');
    if (!this.#grid || !this.#button) return;
    this.#cards = [];
    this.#ids = new Set([this.dataset.currentProduct]);
    this.#pages = new Set();
    this.#nextUrl = this.dataset.nextUrl || '';
    this.#addCards(Array.from(this.#grid.children), false);
    this.querySelector('[data-more-fallback]')?.setAttribute('hidden', '');
    this.#button.addEventListener('click', this.#showMore, { signal: this.#controller.signal });
    window.addEventListener('resize', this.#resize, { passive: true, signal: this.#controller.signal });
    this.dataset.enhanced = 'true';
    this.#paint();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #target() { return visibleBatchSize() * this.#batches; }

  #addCards(cards, append) {
    for (const card of cards) {
      const id = card.dataset?.productId;
      if (!card.matches?.('product-card') || !id || this.#ids.has(id)) {
        if (!append) card.remove();
        continue;
      }
      this.#ids.add(id);
      const node = append ? document.importNode(card, true) : card;
      node.hidden = true;
      this.#cards.push(node);
      if (append) this.#grid.append(node);
    }
  }

  #paint() {
    this.#cards.forEach((card, index) => { card.hidden = index >= this.#target; });
    this.#button.hidden = this.#cards.length <= this.#target && !this.#nextUrl;
    this.#button.disabled = this.#loading;
    this.#button.textContent = this.#loading ? this.dataset.loadingLabel : this.dataset.moreLabel;
    this.hidden = this.#cards.length === 0 && !this.#nextUrl;
  }

  #resize = () => {
    this.#paint();
    if (this.#cards.length < this.#target && this.#nextUrl) this.#load(false);
  };

  #showMore = () => {
    if (this.#loading) return;
    if (this.#cards.length >= this.#target) this.#batches += 1;
    this.#load(true);
  };

  async #load(focusNew) {
    if (this.#loading) return;
    const oldVisible = this.#cards.filter((card) => !card.hidden).length;
    this.#loading = true;
    this.setAttribute('aria-busy', 'true');
    this.#paint();
    if (this.#status) this.#status.textContent = this.dataset.loadingLabel;
    this.querySelector('[data-more-all]')?.setAttribute('hidden', '');
    try {
      while (this.#cards.length < this.#target && this.#nextUrl) {
        const url = new URL(this.#nextUrl, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Unexpected catalog page');
        url.hash = '';
        url.searchParams.set('section_id', this.dataset.sectionId);
        const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
        if (this.#pages.has(url.href)) throw new Error('Repeated catalog page');
        const response = await fetch(url.href, { signal: this.#controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Catalog request failed');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const batch = parsed.querySelector('ik-catalog-more');
        const grid = batch?.querySelector('[data-ik-catalog-batch]');
        if (!grid || batch.dataset.sectionId !== this.dataset.sectionId) throw new Error('Catalog section missing');
        this.#addCards(Array.from(grid.children), true);
        this.#pages.add(url.href);
        this.#nextUrl = batch.dataset.nextUrl || '';
        this.dataset.nextUrl = this.#nextUrl;
      }
      if (this.#status) this.#status.textContent = `${this.dataset.loadedLabel}: ${Math.min(this.#cards.length, this.#target)}`;
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.#status) this.#status.textContent = this.dataset.errorLabel;
      this.querySelector('[data-more-all]')?.removeAttribute('hidden');
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
      this.#paint();
      if (focusNew && this.isConnected) this.#cards[oldVisible]?.querySelector('a')?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('ik-catalog-more')) customElements.define('ik-catalog-more', IkCatalogMore);
